/**
 * useNominationBusinessUnits — the BUs an event must be for, for this caller to nominate to it.
 *
 * Marketing's rule (utils/nominatableEvents): a BD nominates only to events whose BUs include one
 * of theirs. The caller's BUs come from GET /me (`nominationBusinessUnits`), which reports the
 * effective user, so an admin acting as a BD gets that BD's.
 *
 * `businessUnits` is undefined for a caller who does not nominate (canNominate) — no BU rule
 * applies to their views — and `[]` while loading or when the lookup fails, which matches no event:
 * fail-closed, as the service is. `loading` lets a count show a dash rather than a 0 meanwhile.
 *
 * The nomination form reads the same field from its own profile fetch rather than this hook, since
 * it needs the phone and BU from that response too.
 */

import { useEffect, useState } from 'react';
import { eventHubCalendarClient } from '../client/EventCalendarClient';

export interface NominationBusinessUnitsState {
  businessUnits: readonly string[] | undefined;
  loading: boolean;
}

const NONE: readonly string[] = [];

export function useNominationBusinessUnits(mayNominate: boolean): NominationBusinessUnitsState {
  const [businessUnits, setBusinessUnits] = useState<readonly string[] | null>(null);

  useEffect(() => {
    if (!mayNominate) return;
    const controller = new AbortController();
    let cancelled = false;
    setBusinessUnits(null);
    eventHubCalendarClient()
      .getCurrentUserProfile({ signal: controller.signal })
      .then((p) => {
        if (!cancelled) setBusinessUnits(p.nominationBusinessUnits);
      })
      .catch(() => {
        if (!cancelled && !controller.signal.aborted) setBusinessUnits(NONE);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [mayNominate]);

  if (!mayNominate) return { businessUnits: undefined, loading: false };
  return { businessUnits: businessUnits ?? NONE, loading: businessUnits === null };
}
